import { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import styled from "styled-components";
import { SystemManagedDataEntity } from "@lifeat/shared/data-models/system/SystemManagedDataEntity";
import { SpaceCategory, SpaceCategoryLabels } from "@lifeat/shared/data-models/virtual-space/SpaceCategory";
import { VirtualSpace } from "@lifeat/shared/data-models/virtual-space/VirtualSpace";
import { addSpace, buildSpace, changeCategory, changeMetaData, removeSpace } from "@lifeat/shared/spaces/SpacesUtil";
import { Routes } from "@lifeat/shared/system/RouteConstants";
import { LifeAtButtonV2 } from "@common/LifeAtButtonV2/LifeAtButtonV2";
import { useDebounce } from "@hooks/useDebounce";
import { useEvent } from "@hooks/useEvent";
import { callMonolith } from "@service/ServerlessFunctionsService";
import { buildRoute } from "@util/RouteUtil";
import { AdminSpaceDetailsForm } from "./AdminSpaceDetailsForm";

type SyncState =
    | {
          type: "ready";
      }
    | {
          type: "syncing";
      }
    | {
          type: "error";
          message: string;
      };

type Space = SystemManagedDataEntity["spaces"][string];

const Root = styled.div`
    width: 100dvw;
    height: 100dvh;
    padding: 24px;
    background: ${({ theme }) => theme.backgroundPrimary};

    display: flex;
    flex-direction: column;
`;

const Title = styled.h2`
    margin-bottom: 12px;
`;

const Indicator = styled.div`
    font-size: 12px;
`;

const Table = styled.div`
    flex: 1;
    overflow: auto;

    display: grid;
    grid-template-columns: 200px 12ch 24ch 10ch 30ch 5ch 8ch 7ch 14ch 12ch repeat(7, 20ch) 6ch [end];
    grid-auto-rows: auto 120px;
    flex-direction: column;
    align-items: stretch;
    scroll-behavior: smooth;
`;

const TableHeader = styled.div`
    position: sticky;
    top: 0;
    padding: 8px 0;
    background: ${({ theme }) => theme.backgroundPrimary};
    display: grid;
    grid-column: 1 / end;
    grid-template-columns: subgrid;
    grid-auto-flow: column;
    justify-items: start;
    align-items: center;
    gap: 8px;
    font-size: 14px;
`;

const AddSpaceButton = styled(LifeAtButtonV2)`
    position: sticky;
    left: 0;
    width: 100%;
    margin-top: 8px;
`;

function compareSpace(a: { id: string; space: VirtualSpace }, b: { id: string; space: VirtualSpace }) {
    // Removed spaces to the bottom.
    if (a.space.removed !== b.space.removed) {
        return a.space.removed ? 1 : -1;
    }
    // Sorted by recommendation frequency.
    return b.space.recommendation - a.space.recommendation;
}

type AdminSpaceCategoryControlsProps = {
    category: SpaceCategory;
    spaces: NonNullable<SystemManagedDataEntity["spaces"]>;
};
export function AdminSpaceCategoryControls(props: AdminSpaceCategoryControlsProps) {
    const history = useHistory();

    const [syncState, setSyncState] = useState<SyncState>({ type: "ready" });
    const [lastAddedSpaceId, setLastAddedSpaceId] = useState<string | null>(null);

    const syncDraft = useDebounce(async (local: NonNullable<SystemManagedDataEntity["spaces"]>) => {
        try {
            await callMonolith("admin-set-spaces", { spaces: local });
            setSyncState({ type: "ready" });
        } catch (e) {
            setSyncState({ type: "error", message: String(e) });
            alert(String(e));
        }
    }, 1000);

    const [draft, _setDraft] = useState(props.spaces);

    const setDraft = (value: NonNullable<SystemManagedDataEntity["spaces"]>) => {
        _setDraft(value);
        setSyncState({ type: "syncing" });
        syncDraft(value);
    };

    const spacesOfTheCategory = (() => {
        const ret: { id: string; space: Space }[] = [];
        for (const [id, space] of Object.entries(draft)) {
            if (space.category === props.category) {
                ret.push({ id, space });
            }
        }
        ret.sort(compareSpace);
        return ret;
    })();

    const handlePageClose = useEvent((e: BeforeUnloadEvent) => {
        if (syncState.type === "syncing") {
            e.preventDefault();
        }
    });

    useEffect(() => {
        window.addEventListener("beforeunload", handlePageClose);
        return () => window.removeEventListener("beforeunload", handlePageClose);
    }, [handlePageClose]);

    const handleBack = useEvent(() => {
        history.push(buildRoute(Routes.Admin));
    });

    const handleAddSpace = useEvent(() => {
        const newSpace = buildSpace(props.category);
        setLastAddedSpaceId(newSpace.id);
        setDraft(addSpace(draft, newSpace));
    });

    const handleRemoveSpace = useEvent((id: string) => {
        setDraft(removeSpace(draft, id));
    });

    const handleSpaceMetadataChange = useEvent((id: string, next: Space) => {
        setDraft(changeMetaData(draft, id, next));
    });

    const handleCategoryChange = useEvent((spaceId: string, targetCategory: SpaceCategory) => {
        setDraft(changeCategory(draft, spaceId, targetCategory));
    });

    return (
        <Root>
            <LifeAtButtonV2 onClick={handleBack}>{"<"} back</LifeAtButtonV2>
            <Title>Category: {SpaceCategoryLabels[props.category]}</Title>
            <Table>
                <TableHeader>
                    <Indicator>
                        {syncState.type === "ready" && "✅"}
                        {syncState.type === "syncing" && "⏳"}
                        {syncState.type === "error" && "🛑"}
                    </Indicator>
                    <span>Space Id</span>
                    <span>Name</span>
                    <span>Category</span>
                    <span>Video URL</span>
                    <span>Pro</span>
                    <span>Featured</span>
                    <span>Removed</span>
                    <span>Recommendation</span>
                    <span>Creator</span>
                    <span>AVI</span>
                    <span>TikTok</span>
                    <span>YouTube</span>
                    <span>Instagram</span>
                    <span>Spotify</span>
                    <span>Tips</span>
                    <span>Other</span>
                    <span>Delete</span>
                </TableHeader>
                {spacesOfTheCategory.map(({ id, space }) => (
                    <AdminSpaceDetailsForm
                        key={`${id}-${space.recommendation}`}
                        id={id}
                        space={space}
                        scrollOnMount={lastAddedSpaceId === id}
                        onMetadataChange={handleSpaceMetadataChange}
                        onCategoryChange={handleCategoryChange}
                        onDelete={handleRemoveSpace}
                    />
                ))}
            </Table>
            <AddSpaceButton variant="secondary" onClick={handleAddSpace}>
                Add Space
            </AddSpaceButton>
        </Root>
    );
}
